import { useId, type CSSProperties, type ReactNode } from 'react';
import { FieldSize, Size, t } from '@bms2/shared';
import { Icon } from '../Icon';
import styles from './field.module.css';

/**
 * The shell every field in the family renders inside (piece 3, Text Input):
 * the label, the red asterisk of a required field, and the persistent inline
 * error directly below the field, never a toast, tooltip or modal. The Field
 * Format Validators decide whether a value is valid; this decides how invalid
 * looks on screen.
 *
 * 🔨 It also decides how wide the box is, and it always decides (decision
 * 0583): a field is Small, Medium or Large, and a caller that names none gets
 * Medium. The three sizes are the same `Size` a Button reads, so one form
 * speaks one vocabulary. `chars` stays beside them for the answer whose length
 * is exactly known — a mask, a fixed column — and it wins where it is given.
 *
 * 🔒 The width caps the box and nothing else (decision 1322): the label, the
 * hint and the error message run as wide as the column, so a long label reads
 * on one line above a narrow box.
 *
 * 🔒 A fourth width, Column, takes the whole column the field stands in
 * (decision 0647). It is for a cell of a line table, whose columns the table's
 * own width budget sizes, never for a form, where Large stays the bound.
 */
export interface FieldIds {
  inputId: string;
  messageId: string;
}

export interface FieldShellProps {
  label: string;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  hint?: string | null;
  /** Rendered after the label, before the control: the Confidential pill of a Redacted Value. */
  beside?: ReactNode;
  /** The label stays in the accessibility tree and leaves the screen: a field inside a table cell whose column header names it (piece 4). */
  labelHidden?: boolean;
  /**
   * The box's width as its expected answer's characters, where that length is
   * exactly known: a Formatted Input's mask, or a field standing in a column
   * some page sized itself (decision 0297). It wins over `size`.
   */
  chars?: number;
  /**
   * Which of the three widths the box takes (decision 0583): Small for a code
   * or a number, Medium for a name or a reference, Large for a description or
   * an address line. Medium where a caller names none, because a box nobody
   * sized used to take the whole row. Column, in a line table's cell alone,
   * takes the column (decision 0647).
   */
  size?: FieldSize;
  /**
   * 🔒 The Date Picker's own floor (T69; decision 0652): the box never draws
   * narrower than a native date and its calendar button need, whichever size
   * names it. A `min-width` past the size's own `max-width` wins, so this
   * widens Small alone and leaves Medium and Large exactly as they were.
   * 🔒 A date with a time takes a floor of its own, wide enough for the time
   * as well (T562; decision 1324).
   */
  dateFloor?: 'date' | 'datetime' | null;
  children: (ids: FieldIds) => ReactNode;
}

/** The class each of the four widths draws its box at; `chars` uses none of them and sets its own width. */
const SIZE_CLASS: Readonly<Record<FieldSize, string>> = {
  [Size.Small]: styles.small!,
  [Size.Medium]: styles.medium!,
  [Size.Large]: styles.large!,
  [FieldSize.Column]: styles.column!,
};

export function FieldShell({ label, required = false, error = null, disabled = false, hint = null, beside, labelHidden = false, chars, size = Size.Medium, dateFloor = null, children }: FieldShellProps) {
  const inputId = useId();
  const messageId = useId();
  const sized = chars ? '' : SIZE_CLASS[size];
  const className = [styles.field, sized, dateFloor === 'date' ? styles.dateFloor : dateFloor === 'datetime' ? styles.dateTimeFloor : '', error ? styles.invalid : '', disabled ? styles.disabled : ''].filter(Boolean).join(' ');
  return (
    <div
      className={className}
      data-invalid={error ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : 'false'}
      style={chars ? ({ '--field-box-max': `calc(${chars}ch + var(--space-6))` } as CSSProperties) : undefined}
      data-field-chars={chars ?? undefined}
      data-field-size={chars ? undefined : size}
      data-field-date-floor={dateFloor ?? undefined}
    >
      <div className={labelHidden ? styles.labelRowHidden : styles.labelRow}>
        <label className={styles.label} htmlFor={inputId}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden="true">
              {t('field.requiredMark')}
            </span>
          ) : null}
        </label>
        {beside}
      </div>
      <div className={styles.control}>
        {children({ inputId, messageId })}
        {error ? (
          <span className={styles.errorIcon}>
            <Icon meaning="fieldError" label={t('field.errorIcon')} />
          </span>
        ) : null}
      </div>
      {error ? (
        <p id={messageId} className={styles.message} data-field-error="true">
          {error}
        </p>
      ) : null}
      {hint && !error ? <p className={styles.hint}>{hint}</p> : null}
    </div>
  );
}
